import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  CreditCard,
  Info,
  MoreHorizontal,
  TrendingUp,
  Users,
} from "lucide-react";
import { Area, AreaChart, YAxis } from "recharts";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  InsightsDraftDialog,
  type DraftClient,
} from "@/components/insights/InsightsDraftDialog";
import { useFeatureFlagState } from "@/components/PostHog/context";
import { BUSINESS_INSIGHTS_FLAG } from "@/lib/featureFlags";
import { requirePermission } from "@/lib/permissions/permissions";
import { trpc, type RouterOutputs } from "@/lib/trpc/client";
import { createConversationWithClient } from "@/lib/utils/conversationUtils";
import { cn } from "@/lib/utils/ui";

export const Route = createFileRoute("/_auth/_dashboard/insights")({
  beforeLoad: () => {
    requirePermission("packages.view");
  },
  component: InsightsPage,
});

type RangeMonths = 3 | 6 | 12;

type Money = { currency: string; cents: number };

const CHECKIN_TEMPLATE =
  "Hey {name}, haven't heard from you in a bit — how's everything going? No pressure, just want to make sure the plan still fits.";
const REMINDER_TEMPLATE =
  "Hey {name}, just checking in — hope training is still feeling doable. Anything you want to adjust?";
const PAYMENT_TEMPLATE =
  "Hey {name}, your last payment didn’t go through. Whenever you get a minute, updating the card in the app will get you back on track.";

const formatMoney = (cents: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
      currencyDisplay: "narrowSymbol",
    }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(0)}`;
  }
};

const formatAmounts = (amounts: Money[], empty = "—") => {
  if (amounts.length === 0) return empty;
  return amounts
    .map((amount) => formatMoney(amount.cents, amount.currency))
    .join(" · ");
};

const formatAxisTick = (cents: number) => {
  const amount = cents / 100;
  if (amount >= 1000) {
    const thousands = amount / 1000;
    const rounded = Math.round(thousands * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}K`;
  }
  return `${Math.round(amount)}`;
};

const formatPercent = (value: number | null, digits = 1) => {
  if (value == null || !Number.isFinite(value)) return null;
  const rounded = Number(value.toFixed(digits));
  const prefix = rounded > 0 ? "+" : "";
  return { text: `${prefix}${rounded}%`, positive: rounded >= 0, rounded };
};

const initials = (firstName: string, lastName: string) => {
  const value = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  return value || "?";
};

const fullName = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`.trim() || "Client";

const formatActivity = (value: string | null) => {
  if (!value) return "No activity";
  const then = Date.parse(value);
  if (!Number.isFinite(then)) return "No activity";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 8) return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  const months = Math.max(1, Math.round(days / 30));
  return months === 1 ? "1 month ago" : `${months} months ago`;
};

function InsightsPage() {
  const navigate = useNavigate();
  const insightsFlag = useFeatureFlagState(BUSINESS_INSIGHTS_FLAG);
  const insightsEnabled = insightsFlag === "on";
  const [rangeMonths, setRangeMonths] = useState<RangeMonths>(6);
  const summary = trpc.insights.summary.useQuery(
    { rangeMonths },
    { enabled: insightsEnabled },
  );

  useEffect(() => {
    if (insightsFlag === "off") {
      void navigate({ to: "/clients", replace: true });
    }
  }, [insightsFlag, navigate]);
  const [selected, setSelected] = useState<string[]>([]);
  const [draft, setDraft] = useState<{
    clients: DraftClient[];
    template: string;
    title: string;
  } | null>(null);

  const data = summary.data;
  const messageable = useMemo(
    () => (data?.atRisk ?? []).filter((row) => row.canMessage).map((row) => row.clientId),
    [data?.atRisk],
  );

  const openChat = async (clientId: string) => {
    try {
      const conversationId = await createConversationWithClient({ clientId });
      await navigate({ to: "/chat", search: { conversationId } });
    } catch (error) {
      console.error(error);
      toast.error("Could not open chat");
    }
  };

  const openDraft = (
    clients: DraftClient[],
    template: string,
    title: string,
  ) => {
    if (clients.length === 0) return;
    setDraft({ clients, template, title });
  };

  if (!insightsEnabled) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="flex w-full flex-col gap-6 p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Business Insights
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              A snapshot of your business performance, revenue and client health.
            </p>
          </div>
          <Select
            value={String(rangeMonths)}
            onValueChange={(value) => setRangeMonths(Number(value) as RangeMonths)}
          >
            <SelectTrigger className="w-[180px]">
              <Calendar className="size-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {summary.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading insights…</p>
        ) : summary.isError || !data ? (
          <p className="text-sm text-muted-foreground">
            Insights could not be loaded. Refresh the page to try again.
          </p>
        ) : data.empty ? (
          <EmptyState kind={data.empty} />
        ) : (
          <>
            <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.9fr)]">
              <MrrCard data={data} />
              <Card className="h-full">
                <CardContent className="flex h-full flex-col justify-center divide-y p-0">
                  <MetricRow
                    icon={<Users className="size-4" />}
                    label="Active paying clients"
                    value={String(data.activePayingClients.count)}
                  />
                  <MetricRow
                    icon={<CreditCard className="size-4" />}
                    label="Revenue this month"
                    value={formatAmounts(data.revenue.thisMonth)}
                  />
                  <MetricRow
                    icon={<TrendingUp className="size-4" />}
                    label="3-month churn rate"
                    value={
                      data.churnPercent == null
                        ? "—"
                        : `${Number(data.churnPercent.toFixed(1))}%`
                    }
                    hint="Clients whose paid subscription ended in the last 3 months and who are not paying now, divided by paying clients at the start of that window plus new paying clients. A pause is not churn, and a client who comes back is not counted."
                  />
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <SummaryCard
                label="Average revenue per client"
                value={formatAmounts(data.averageRevenuePerClient.current)}
                change={formatPercent(data.averageRevenuePerClient.changePercent)}
                detail={`vs. previous ${rangeMonths} months`}
              />
              <SummaryCard
                label="Average client tenure"
                value={
                  data.tenureMonths == null
                    ? "—"
                    : `${data.tenureMonths.toFixed(1)} months`
                }
                change={formatPercent(data.tenureChangePercent)}
                detail={`vs. previous ${rangeMonths} months`}
              />
              <SummaryCard
                label="Lifetime value (LTV)"
                value={formatAmounts(data.ltv)}
                badge={data.earlyEstimate ? "Early estimate" : null}
                detail="Based on average revenue and tenure"
              />
            </div>

            <FailedPayments
              rows={data.failedPayments}
              totalCount={data.failedPaymentsCount}
              totals={data.failedPaymentsTotals}
              onDraft={(row) =>
                openDraft([row], PAYMENT_TEMPLATE, "Payment reminder")
              }
            />

            <AtRiskTable
              rows={data.atRisk}
              totalCount={data.atRiskCount}
              totals={data.atRiskTotals}
              selected={selected}
              messageable={messageable}
              onSelectedChange={setSelected}
              onDraft={(row) =>
                openDraft(
                  [row],
                  row.draft === "reminder" ? REMINDER_TEMPLATE : CHECKIN_TEMPLATE,
                  row.draft === "reminder" ? "Draft reminder" : "Draft check-in",
                )
              }
              onBulkDraft={() => {
                const chosen = data.atRisk.filter((row) =>
                  selected.includes(row.clientId),
                );
                openDraft(chosen, CHECKIN_TEMPLATE, "Draft check-in");
              }}
              onOpenChat={(clientId) => void openChat(clientId)}
            />
          </>
        )}
      </div>
      <InsightsDraftDialog
        open={draft != null}
        onOpenChange={(open) => {
          if (!open) setDraft(null);
        }}
        clients={draft?.clients ?? []}
        template={draft?.template ?? CHECKIN_TEMPLATE}
        title={draft?.title ?? "Draft check-in"}
      />
    </TooltipProvider>
  );
}

function EmptyState({ kind }: { kind: "no_clients" | "no_payments" }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3 py-10">
        <h2 className="text-lg font-medium">
          {kind === "no_clients"
            ? "Insights start with your first client"
            : "Connect payments to see the numbers"}
        </h2>
        <p className="max-w-xl text-sm text-muted-foreground">
          {kind === "no_clients"
            ? "Once a client is on a package, this page shows revenue, retention, and who may benefit from a check-in."
            : "Revenue, churn, and the check-in list are calculated from payments collected in Trainrr. Set up a package to get started."}
        </p>
        <Button asChild>
          <Link to={kind === "no_clients" ? "/clients" : "/packages"}>
            {kind === "no_clients" ? "Go to clients" : "Set up packages"}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function MrrCard({
  data,
}: {
  data: RouterOutputs["insights"]["summary"];
}) {
  const currency =
    data.mrr.current[0]?.currency ??
    data.mrr.series.flatMap((point) => point.amounts)[0]?.currency ??
    "USD";
  const chartData = data.mrr.series.map((point) => ({
    label: point.label,
    value: point.amounts.find((amount) => amount.currency === currency)?.cents ?? 0,
  }));
  const chartMax = Math.max(...chartData.map((point) => point.value), 0);
  const chartCeiling = (() => {
    const amount = chartMax / 100;
    if (amount <= 0) return 100;
    const magnitude = 10 ** Math.floor(Math.log10(amount));
    const scaled = amount / magnitude;
    const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
    return nice * magnitude * 100;
  })();
  const chartTicks = [0, chartCeiling / 2, chartCeiling];
  const change = formatPercent(data.mrr.changePercent, 0);
  const previous = data.mrr.previous.find((amount) => amount.currency === currency);

  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col justify-center gap-4 p-6 sm:flex-row sm:items-center">
        <div className="shrink-0 space-y-1">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            Monthly Recurring Revenue
            <Hint text="List price of active subscriptions. Weekly prices are multiplied by 52/12 and yearly prices are divided by 12. Promo discounts show up in revenue this month, not in this figure." />
          </div>
          <div className="text-4xl font-semibold tracking-tight">
            {formatAmounts(data.mrr.current)}
          </div>
          {change && previous ? (
            <p className="flex items-center gap-1.5 text-sm">
              <span
                className={cn(
                  "flex items-center gap-0.5 font-medium",
                  change.positive ? "text-emerald-600" : "text-red-600",
                )}
              >
                {change.positive ? (
                  <ArrowUpRight className="size-4" />
                ) : (
                  <ArrowDownRight className="size-4" />
                )}
                {Math.abs(change.rounded)}%
              </span>
              <span className="text-muted-foreground">
                vs. {formatMoney(previous.cents, previous.currency)} last month
              </span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">vs. — last month</p>
          )}
        </div>
        <ChartContainer
          config={{ value: { label: "MRR", color: "hsl(var(--primary))" } }}
          className="aspect-auto h-24 w-full min-w-0 sm:h-28 sm:flex-1"
        >
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 4, bottom: 12, left: 0 }}
          >
            <YAxis
              orientation="right"
              width={40}
              axisLine={false}
              tickLine={false}
              domain={[0, chartCeiling]}
              ticks={chartTicks}
              tickFormatter={(value) => formatAxisTick(Number(value))}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value) => formatMoney(Number(value), currency)}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="var(--color-value)"
              fill="var(--color-value)"
              fillOpacity={0.15}
              strokeWidth={2}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

function MetricRow({
  icon,
  label,
  value,
  hint,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-1 items-center justify-between gap-6 px-5 py-4">
      <div className="flex items-center gap-2.5 whitespace-nowrap text-sm text-muted-foreground">
        {icon}
        <span>{label}</span>
        {hint ? <Hint text={hint} /> : null}
      </div>
      <div className="whitespace-nowrap text-base font-semibold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  detail,
  change,
  badge,
}: {
  label: string;
  value: string;
  detail: string;
  change?: { text: string; positive: boolean } | null;
  badge?: string | null;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-2xl font-semibold tracking-tight">{value}</div>
          {change ? (
            <Badge
              variant="secondary"
              className={cn(
                change.positive
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-700",
              )}
            >
              {change.text}
            </Badge>
          ) : null}
          {badge ? (
            <Badge variant="outline" className="text-muted-foreground">
              {badge}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

function Hint({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-muted-foreground" aria-label="More info">
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-left">{text}</TooltipContent>
    </Tooltip>
  );
}

function ClientName({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 items-center justify-center rounded-full bg-muted text-xs font-medium">
        {initials(firstName, lastName)}
      </div>
      <span className="font-medium">{fullName(firstName, lastName)}</span>
    </div>
  );
}

function FailedPayments({
  rows,
  totalCount,
  totals,
  onDraft,
}: {
  rows: Array<
    DraftClient & { amounts: Money[]; canMessage: boolean }
  >;
  totalCount: number;
  totals: Money[];
  onDraft: (row: DraftClient) => void;
}) {
  return (
    <section className="space-y-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">Failed or overdue payments</h2>
          <Badge variant="secondary">{totalCount}</Badge>
          {totalCount > 0 ? (
            <span className="text-sm text-muted-foreground">
              {formatAmounts(totals)} outstanding
            </span>
          ) : null}
        </div>
        {totalCount > rows.length ? (
          <p className="text-sm text-muted-foreground">
            Showing the {rows.length} largest amounts.
          </p>
        ) : null}
        <p className="text-sm text-muted-foreground">
          These payments failed or are past due.
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No failed or overdue payments.</p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.clientId}>
                  <TableCell>
                    <ClientName firstName={row.firstName} lastName={row.lastName} />
                  </TableCell>
                  <TableCell>{formatAmounts(row.amounts)}</TableCell>
                  <TableCell className="text-right">
                    {row.canMessage ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDraft(row)}
                      >
                        Draft reminder
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function AtRiskTable({
  rows,
  totalCount,
  totals,
  selected,
  messageable,
  onSelectedChange,
  onDraft,
  onBulkDraft,
  onOpenChat,
}: {
  rows: Array<
    DraftClient & {
      amounts: Money[];
      lastActivityAt: string | null;
      canMessage: boolean;
      canAccess: boolean;
      draft: "checkin" | "reminder";
      reasons: { kind: string; title: string; detail: string }[];
    }
  >;
  totalCount: number;
  totals: Money[];
  selected: string[];
  messageable: string[];
  onSelectedChange: (ids: string[]) => void;
  onDraft: (row: (typeof rows)[number]) => void;
  onBulkDraft: () => void;
  onOpenChat: (clientId: string) => void;
}) {
  const allChecked =
    messageable.length > 0 && messageable.every((id) => selected.includes(id));

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">Clients to check in with</h2>
            <Badge variant="secondary">{totalCount}</Badge>
            {totalCount > 0 ? (
              <span className="text-sm text-muted-foreground">
                {formatAmounts(totals)}/mo at risk
              </span>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            These clients are currently paying and may benefit from a check-in.
          </p>
          {totalCount > rows.length ? (
            <p className="text-sm text-muted-foreground">
              Showing the {rows.length} largest amounts.
            </p>
          ) : null}
        </div>
        {selected.length > 0 ? (
          <Button size="sm" onClick={onBulkDraft}>
            Draft check-in
          </Button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No clients need a check-in right now.
        </p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={(checked) =>
                      onSelectedChange(checked ? messageable : [])
                    }
                    aria-label="Select clients you can message"
                    disabled={messageable.length === 0}
                  />
                </TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Signal</TableHead>
                <TableHead>Monthly value</TableHead>
                <TableHead>Last activity</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.clientId}>
                  <TableCell>
                    <Checkbox
                      checked={selected.includes(row.clientId)}
                      disabled={!row.canMessage}
                      onCheckedChange={(checked) =>
                        onSelectedChange(
                          checked
                            ? [...selected, row.clientId]
                            : selected.filter((id) => id !== row.clientId),
                        )
                      }
                      aria-label={`Select ${fullName(row.firstName, row.lastName)}`}
                    />
                  </TableCell>
                  <TableCell>
                    <ClientName firstName={row.firstName} lastName={row.lastName} />
                  </TableCell>
                  <TableCell>
                    {row.reasons.length === 0 ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                    <div className="space-y-2">
                      {row.reasons.map((reason) => (
                        <div key={reason.kind} className="flex gap-2">
                          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-amber-500" />
                          <div>
                            <div className="text-sm font-medium">{reason.title}</div>
                            <div className="text-xs text-muted-foreground">
                              {reason.detail}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </TableCell>
                  <TableCell>{formatAmounts(row.amounts)}/mo</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatActivity(row.lastActivityAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      {row.canMessage ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDraft(row)}
                        >
                          {row.draft === "reminder"
                            ? "Draft reminder"
                            : "Draft check-in"}
                        </Button>
                      ) : null}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="More actions">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {row.canAccess ? (
                            <DropdownMenuItem asChild>
                              <Link
                                to="/clients/$clientId"
                                params={{ clientId: row.clientId }}
                              >
                                View client
                              </Link>
                            </DropdownMenuItem>
                          ) : null}
                          {row.canMessage ? (
                            <DropdownMenuItem
                              onClick={() => onOpenChat(row.clientId)}
                            >
                              Open chat
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
